// Operator's answer view — /tarjouspyynnot/:id. Request recap left,
// offer form right (stacked on mobile, request first). Layout decision
// (own page, not modal): spec §6. Won/ended branches render read-only;
// keikka confirmation stays on betoni.online (spec §2 non-goal).

import { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router";
import { Alert, Badge, Button, Grid, Stack, TextField, Typography } from "@mui/material";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import BlockIcon from "@mui/icons-material/Block";
import UndoIcon from "@mui/icons-material/Undo";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutlined";
import { deriveOfferLifecycle, isOfferLocked } from "@ibetoni/jerry-client";
import { MessageThread } from "@ibetoni/messaging-ui";
import { useT } from "src/i18n";
import MessageThreadDialog from "src/components/common/MessageThreadDialog";
import GlassDialog from "src/components/common/GlassDialog";
import { useAuth } from "src/context/AuthContext";
import { API_URL } from "src/utils/apiUrl";
import { providerApi } from "src/utils/tarjouspyynnot/providerApi";
import { authHeaders, jsonOrThrow } from "src/utils/apiFetch";
import { captureError } from "src/utils/errorReporting";
import { listSupportMine, findSupportThreadFor } from "src/utils/tuki/tukiApi";
import TilaaBackdrop from "src/components/quote/TilaaBackdrop";
import StepHeader from "src/components/quote/StepHeader";
import FrostedCard from "src/components/quote/FrostedCard";
import RequestRecap from "src/components/tarjouspyynnot/RequestRecap";
import OfferForm from "src/components/tarjouspyynnot/OfferForm";

export default function TarjouspyyntoDetail() {
    const { id: idParam } = useParams();
    const id = Number(idParam);
    const { token, user, logout } = useAuth();
    const operatorPersonId = user?.person?.[0]?.personId;
    const navigate = useNavigate();
    const t = useT();

    const [detail, setDetail] = useState(null);
    const [error, setError] = useState(null);
    const [forbidden, setForbidden] = useState(false);

    const [supportOpen, setSupportOpen] = useState(false);
    const [supportThreadId, setSupportThreadId] = useState(null);
    const [supportDraft, setSupportDraft] = useState("");
    const [supportBusy, setSupportBusy] = useState(false);
    const [supportError, setSupportError] = useState(null);
    const [supportUnread, setSupportUnread] = useState(0);

    // Decline ("En tarjoa") + un-decline + pre-offer conversation state.
    const [declineOpen, setDeclineOpen] = useState(false);
    const [declineReason, setDeclineReason] = useState("");
    const [declineBusy, setDeclineBusy] = useState(false);
    const [declineError, setDeclineError] = useState(null);
    const [undeclineBusy, setUndeclineBusy] = useState(false);
    const [chatBusy, setChatBusy] = useState(false);

    // Withdraw ("Peru tarjous") of a sent, not-yet-accepted offer.
    const [withdrawOpen, setWithdrawOpen] = useState(false);
    const [withdrawBusy, setWithdrawBusy] = useState(false);
    const [withdrawError, setWithdrawError] = useState(null);

    // Per-request support-thread lookup: pre-wires the existing conversation
    // into the dialog and drives the unread badge on the button. Unread
    // semantics match /tuki and the account-menu badge (spec §2). Fail-quiet:
    // a lookup error must never block the page — the badge just doesn't render.
    const refreshSupportThread = useCallback(async () => {
        try {
            const items = await listSupportMine({ token, status: "all" });
            const match = findSupportThreadFor(items, id);
            if (match) {
                setSupportThreadId(match.threadId);
                setSupportUnread(Number(match.unreadCount) || 0);
            } else {
                setSupportUnread(0);
            }
        } catch (err) {
            if (err?.status === 401) { logout(); return; }
            captureError(err, {
                tags: { entity: "messageThread", operation: "support_mine_detail" },
                extra: { pumppuRequestId: id },
            });
        }
    }, [token, id, logout]);

    const fetchDetail = useCallback(async ({ refresh = false } = {}) => {
        try {
            const d = await providerApi(token).getProviderDetail(id);
            setDetail(d);
        } catch (err) {
            if (err.status === 401) { logout(); return; }
            if (err.status === 403) { setForbidden(true); return; }
            if (err.status !== 404) {
                captureError(err, {
                    tags: { entity: "pumppuRequest", operation: "provider_detail" },
                    extra: { pumppuRequestId: id },
                });
            }
            // A failed refresh (other than 401/403 above) after a successful action
            // keeps the page as is; only the first load swaps it for the error view.
            if (!refresh) setError(err);
        }
    }, [token, id, logout]);
    const refreshDetail = useCallback(() => fetchDetail({ refresh: true }), [fetchDetail]);

    useEffect(() => {
        if (!Number.isFinite(id)) {
            navigate("/tarjouspyynnot", { replace: true });
            return;
        }
        async function run() {
            await fetchDetail();
        }
        run();
    }, [id, fetchDetail, navigate]);

    useEffect(() => {
        if (!detail?.request) return;
        document.title = t("provider.detail.pageTitle", {
            address: detail.request.formattedAddress || detail.request.osoite,
        });
        return () => { document.title = t("provider.detail.pageTitleFallback"); };
    }, [detail, t]);

    useEffect(() => {
        if (!Number.isFinite(id)) return;
        async function run() { await refreshSupportThread(); }
        run();
    }, [id, refreshSupportThread]);

    // Foreground push (SW posts FCM_MESSAGE_RECEIVED to the focused tab instead
    // of an OS notification — ValuDetail pattern). Any push aimed at an operator
    // tab is currently a support reply; the lookup is one cheap GET, so no
    // payload filtering (spec §4).
    useEffect(() => {
        if (typeof navigator === "undefined" || !navigator.serviceWorker) return;
        const onMessage = (event) => {
            if (event.data?.type !== "FCM_MESSAGE_RECEIVED") return;
            refreshSupportThread();
        };
        navigator.serviceWorker.addEventListener("message", onMessage);
        return () => navigator.serviceWorker.removeEventListener("message", onMessage);
    }, [refreshSupportThread]);

    if (forbidden) {
        return (
            <TilaaBackdrop>
                <FrostedCard sx={{ p: 2 }}>
                    <Typography variant="body2">{t("provider.detail.providersOnlyNotice")}</Typography>
                </FrostedCard>
            </TilaaBackdrop>
        );
    }
    if (error?.status === 404) {
        return (
            <TilaaBackdrop>
                <Typography variant="body1">{t("provider.detail.notFound")}</Typography>
                <Button component={RouterLink} to="/tarjouspyynnot" sx={{ mt: 2 }}>
                    {t("provider.detail.backToRequests")}
                </Button>
            </TilaaBackdrop>
        );
    }
    if (error) {
        return (
            <TilaaBackdrop>
                <Typography variant="body2" color="error">{t("provider.detail.loadFailed")}</Typography>
            </TilaaBackdrop>
        );
    }
    if (!detail) {
        return (
            <TilaaBackdrop>
                <Typography variant="body2">{t("common.loading")}</Typography>
            </TilaaBackdrop>
        );
    }

    const { request, ownOffer, ownAttachments, requestAttachments, ownDecline } = detail;
    const { isWon, isEnded, endedReasonLabel } = deriveOfferLifecycle({ request, ownOffer });
    const isDeclined = Boolean(ownDecline);
    // Decline only makes sense while the request is live and the operator has no
    // offer yet (a started offer is withdrawn, not declined — mirrors the backend).
    const canDecline = !isWon && !isEnded && !isDeclined && !ownOffer;
    // Withdraw a sent offer while the request is live. Once it ends the offer can
    // no longer be accepted (fb#2317), so there is nothing to withdraw.
    const canWithdraw = !isEnded && ownOffer?.status === "pending";

    // Create (or reuse) the support thread with the operator's own first
    // message. THIS is the escalation moment that fires the admin heads-up
    // email — merely opening the dialog must never notify anyone.
    const sendFirstSupportMessage = async () => {
        const body = supportDraft.trim();
        if (!body || supportBusy) return;
        setSupportBusy(true);
        setSupportError(null);
        try {
            const res = await fetch(`${API_URL}/api/messages/support`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...authHeaders(token) },
                body: JSON.stringify({
                    contextType: "pumppuRequest",
                    contextId: request.pumppuRequestId,
                    body,
                }),
            });
            const data = await jsonOrThrow(res);
            if (!data?.threadId) {
                setSupportError(t("provider.detail.supportThreadOpenFailed"));
                return;
            }
            setSupportThreadId(data.threadId);
            setSupportDraft("");
        } catch (err) {
            if (err.status === 401) { logout(); return; }
            // 4xx = expected validation/permission → show the backend's Finnish
            // message, don't report to Sentry. Report only unexpected (5xx/network).
            const is4xx = err.status >= 400 && err.status < 500;
            if (!is4xx) {
                captureError(err, {
                    tags: { entity: "pumppuRequest", operation: "support_open" },
                    extra: { pumppuRequestId: request.pumppuRequestId },
                });
            }
            setSupportError(
                is4xx && err.body?.message
                    ? err.body.message
                    : t("provider.detail.supportContactFailed")
            );
        } finally {
            setSupportBusy(false);
        }
    };

    // Close the dialog and discard any unsent draft / transient error. Re-run
    // the lookup: MessageThread marked messages read while open, so the badge
    // clears (and a just-created thread's id is re-confirmed server-side).
    const closeSupport = () => {
        setSupportOpen(false);
        setSupportDraft("");
        setSupportError(null);
        refreshSupportThread();
    };

    // Decline the whole request (no offer) with a free-text reason. On success the
    // request leaves the operator's Avoimet tab and the customer is notified.
    const submitDecline = async () => {
        if (declineBusy) return;
        setDeclineBusy(true);
        setDeclineError(null);
        const result = await providerApi(token).declineRequest(request.pumppuRequestId, declineReason.trim() || null)
            .catch((err) => { if (err.status === 401) { logout(); return null; } throw err; });
        setDeclineBusy(false);
        if (!result) return;
        if (!result.success) {
            setDeclineError(result.error || t("provider.detail.declineFailed"));
            return;
        }
        setDeclineOpen(false);
        setDeclineReason("");
        await refreshDetail();
    };

    // Withdraw the sent offer. On success it moves Tarjotut → Päättyneet and the
    // customer can no longer select it.
    const submitWithdraw = async () => {
        if (withdrawBusy) return;
        setWithdrawBusy(true);
        setWithdrawError(null);
        const result = await providerApi(token).withdrawOffer(request.pumppuRequestId, ownOffer.pumppuOfferId)
            .catch((err) => { if (err.status === 401) { logout(); return null; } throw err; });
        setWithdrawBusy(false);
        if (!result) return;
        if (!result.success) {
            setWithdrawError(result.error || t("provider.detail.withdrawFailed"));
            // 409 = no longer pending (e.g. accepted meanwhile) — show the real state.
            if (result.status === 409) await refreshDetail();
            return;
        }
        setWithdrawOpen(false);
        await refreshDetail();
    };

    // Reverse a prior decline — the request returns to Avoimet and is offerable again.
    const submitUndecline = async () => {
        if (undeclineBusy) return;
        setUndeclineBusy(true);
        const result = await providerApi(token).undeclineRequest(request.pumppuRequestId)
            .catch((err) => { if (err.status === 401) { logout(); return null; } throw err; });
        setUndeclineBusy(false);
        if (result?.success) await refreshDetail();
    };

    // Open (ensure) the customer chat thread BEFORE making an offer, then reveal
    // the inline chat panel by refetching the detail (which returns messageThreadId).
    const startConversation = async () => {
        if (chatBusy) return;
        setChatBusy(true);
        const result = await providerApi(token).startConversation(request.pumppuRequestId)
            .catch((err) => { if (err.status === 401) { logout(); return null; } throw err; });
        setChatBusy(false);
        if (result?.success) await refreshDetail();
    };

    return (
        <TilaaBackdrop>
            <Button component={RouterLink} to="/tarjouspyynnot" size="small" sx={{ mb: 2 }}>
                {t("provider.detail.backToList")}
            </Button>

            <StepHeader>{request.formattedAddress || request.osoite}</StepHeader>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
                {t("provider.detail.requestIdLabel", { id: request.pumppuRequestId })}
            </Typography>

            {isWon && (
                <Alert
                    severity="success"
                    sx={{ mb: 2 }}
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            href="https://betoni.online/tarjouspyynnot"
                            target="_blank"
                            rel="noopener"
                        >
                            {t("provider.detail.confirmScheduleCta")}
                        </Button>
                    }
                >
                    {t("provider.detail.wonNotice")}
                </Alert>
            )}
            {isEnded && (
                <Alert severity="info" sx={{ mb: 2 }}>{endedReasonLabel}</Alert>
            )}

            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <RequestRecap request={request} requestAttachments={requestAttachments} />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <FrostedCard sx={{ p: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>{t("provider.detail.yourOfferHeading")}</Typography>
                        {isDeclined ? (
                            <Stack spacing={1.5}>
                                <Alert severity="info">
                                    {t("provider.detail.declinedNotice")}
                                    {ownDecline.reason ? t("provider.detail.declinedReasonSuffix", { reason: ownDecline.reason }) : ""}
                                </Alert>
                                <Button
                                    variant="outlined"
                                    onClick={submitUndecline}
                                    disabled={undeclineBusy}
                                    sx={{ alignSelf: "flex-start" }}
                                >
                                    {t("provider.detail.undeclineCta")}
                                </Button>
                            </Stack>
                        ) : isEnded && !ownOffer ? (
                            <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                {t("provider.detail.endedNoOfferNotice")}
                            </Typography>
                        ) : (
                            <>
                                {/* OfferForm seeds its state from ownOffer once: remount only
                                    when the offer becomes locked (withdrawn, accepted, …), not
                                    on save/send, which it tracks itself. onSubmitted refetches
                                    so a just-sent offer shows the withdraw button without a reload. */}
                                <OfferForm
                                    key={isOfferLocked(ownOffer?.status) ? "locked" : "open"}
                                    pumppuRequestId={request.pumppuRequestId}
                                    ownOffer={ownOffer}
                                    ownAttachments={ownAttachments}
                                    onSubmitted={refreshDetail}
                                />
                                {canWithdraw && (
                                    <Button
                                        size="small"
                                        color="inherit"
                                        startIcon={<UndoIcon />}
                                        onClick={() => setWithdrawOpen(true)}
                                        sx={{ mt: 1.5, color: "text.secondary" }}
                                    >
                                        {t("provider.detail.withdrawCta")}
                                    </Button>
                                )}
                                {canDecline && (
                                    <Button
                                        size="small"
                                        color="inherit"
                                        startIcon={<BlockIcon />}
                                        onClick={() => setDeclineOpen(true)}
                                        sx={{ mt: 1.5, color: "text.secondary" }}
                                    >
                                        {t("provider.shared.declineCta")}
                                    </Button>
                                )}
                            </>
                        )}

                        <Badge badgeContent={supportUnread} color="error" max={99} sx={{ mt: 2 }}>
                            <Button
                                size="small"
                                startIcon={<SupportAgentIcon />}
                                onClick={() => setSupportOpen(true)}
                            >
                                {t("provider.detail.contactSupportCta")}
                            </Button>
                        </Badge>
                    </FrostedCard>
                </Grid>
            </Grid>

            {detail.messageThreadId && operatorPersonId ? (
                <FrostedCard sx={{ p: 2, mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                        {t("provider.detail.chatHeading")}
                    </Typography>
                    <MessageThread
                        threadId={detail.messageThreadId}
                        currentPersonId={operatorPersonId}
                        token={token}
                        apiBaseUrl={API_URL}
                        height={360}
                    />
                </FrostedCard>
            ) : operatorPersonId && !isEnded && (
                // Pre-offer Q&A: no thread exists yet (created lazily on first
                // offer draft). Let the operator open one to ask a question first.
                <FrostedCard sx={{ p: 2, mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>{t("provider.detail.chatHeading")}</Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>
                        {t("provider.detail.chatPreOfferHint")}
                    </Typography>
                    <Button
                        variant="outlined"
                        startIcon={<ChatBubbleOutlineIcon />}
                        onClick={startConversation}
                        disabled={chatBusy}
                    >
                        {t("provider.detail.askCustomerCta")}
                    </Button>
                </FrostedCard>
            )}

            <MessageThreadDialog
                open={supportOpen}
                onClose={closeSupport}
                title={t("provider.detail.supportDialogTitle")}
                threadId={supportThreadId}
                currentPersonId={operatorPersonId}
                token={token}
                apiBaseUrl={API_URL}
            >
                <Stack spacing={1.5} sx={{ pt: 1 }}>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        {t("provider.detail.supportIntro")}
                    </Typography>
                    {supportError && (
                        <Alert severity="error" onClose={() => setSupportError(null)}>
                            {supportError}
                        </Alert>
                    )}
                    <TextField
                        value={supportDraft}
                        onChange={(e) => setSupportDraft(e.target.value)}
                        placeholder={t("provider.detail.supportPlaceholder")}
                        multiline
                        minRows={3}
                        maxRows={8}
                        fullWidth
                        autoFocus
                        disabled={supportBusy}
                    />
                    <Button
                        variant="contained"
                        startIcon={<SupportAgentIcon />}
                        onClick={sendFirstSupportMessage}
                        disabled={!supportDraft.trim() || supportBusy}
                        sx={{ alignSelf: "flex-end" }}
                    >
                        {t("provider.detail.sendToSupportCta")}
                    </Button>
                </Stack>
            </MessageThreadDialog>

            <GlassDialog
                open={declineOpen}
                onClose={() => { setDeclineOpen(false); setDeclineError(null); }}
                busy={declineBusy}
                title={t("provider.shared.declineCta")}
            >
                <Stack spacing={1.5}>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        {t("provider.detail.declineDialogIntro")}
                    </Typography>
                    {declineError && (
                        <Alert severity="error" onClose={() => setDeclineError(null)}>{declineError}</Alert>
                    )}
                    <TextField
                        value={declineReason}
                        onChange={(e) => setDeclineReason(e.target.value)}
                        placeholder={t("provider.detail.declineReasonPlaceholder")}
                        multiline
                        minRows={3}
                        maxRows={8}
                        fullWidth
                        autoFocus
                        disabled={declineBusy}
                        slotProps={{ htmlInput: { maxLength: 500 } }}
                    />
                    <Button
                        variant="contained"
                        color="error"
                        startIcon={<BlockIcon />}
                        onClick={submitDecline}
                        disabled={declineBusy}
                        sx={{ alignSelf: "flex-end" }}
                    >
                        {t("provider.detail.confirmDeclineCta")}
                    </Button>
                </Stack>
            </GlassDialog>

            <GlassDialog
                open={withdrawOpen}
                onClose={() => { setWithdrawOpen(false); setWithdrawError(null); }}
                busy={withdrawBusy}
                title={t("provider.detail.withdrawCta")}
            >
                <Stack spacing={1.5}>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        {t("provider.detail.withdrawDialogIntro")}
                    </Typography>
                    {withdrawError && (
                        <Alert severity="error" onClose={() => setWithdrawError(null)}>{withdrawError}</Alert>
                    )}
                    <Button
                        variant="contained"
                        color="error"
                        startIcon={<UndoIcon />}
                        onClick={submitWithdraw}
                        disabled={withdrawBusy}
                        sx={{ alignSelf: "flex-end" }}
                    >
                        {t("provider.detail.confirmWithdrawCta")}
                    </Button>
                </Stack>
            </GlassDialog>
        </TilaaBackdrop>
    );
}
